import { useState, useRef, useEffect, useCallback, type FC, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGlobalNotification } from '../contexts/GlobalNotificationContext';
import { usePrimeirosPassos } from '../contexts/PrimeirosPassosContext';
import { useTheme } from '../contexts/ThemeContext';
import { getUserPermissions, ehAdministradorDaEmpresa, hasPermission, PERMISSOES } from '../config/permissions';
import { GRUPOS_OPERACAO, itensVisiveis, type ItemNav } from '../config/navegacao';
import { CELULAR_MAX } from '../hooks/useCelular';
import { lerResumoAssinatura } from '../services/billingService';
import PlanoModal from './PlanoModal';
import SuporteModal from './SuporteModal';
import InstalarBrik from './InstalarBrik';
import AssistenteService, {
    CONVERSAS_ASSISTENTE_EVENT,
    type ConversaAssistente,
} from '../services/AssistenteService';
import BrikMark from './BrikMark';
import { abrirBuscaGlobal } from './BuscaGlobal';
import NotificationsBell from './NotificationsBell';
/* Os ícones dos itens de navegação moram em `config/navegacao.ts`, junto do
   resto da definição de cada item. Aqui ficam só os da casca. */
import {
    LuSearch,
    LuClock,
    LuTriangleAlert,
    LuHistory,
    LuPanelLeft,
    LuSun,
    LuMoon,
    LuLogOut,
    LuChevronDown,
    LuSettings,
    LuMessageSquare,
    LuCreditCard,
    LuCircleHelp,
    LuRocket,
    LuDownload,
    LuChevronRight
} from 'react-icons/lu';
import './Sidebar.css';

interface SidebarProps {
    onLogout: () => void;
}

/* Sobraram duas seções: Gestão de usuários, Administração, Chatbot e Empresa
   foram inteiras para a área de Configurações em 2026-09-10. O mapa delas
   está em `src/config/configuracoes.ts`. */
type SectionKey = 'marketing' | 'assistente';

/* Rotas de cada seção — usadas para abrir sozinha a seção da tela atual */
const SECTION_ROUTES: Record<SectionKey, string[]> = {
    marketing: ['/campanhas', '/campaign-templates', '/pagina-de-captura'],
    assistente: ['/assistente'],
};

/* Quantas conversas o menu mostra. Cinco cabem sem empurrar o rodapé e cobrem
   o "voltar ao que eu estava vendo"; o resto fica no "Ver todas". */
const CONVERSAS_NO_MENU = 5;

const COLLAPSED_KEY = 'lemeai_sidebar_collapsed';
/* Se da última vez faltavam passos. Serve só para a pílula nascer no lugar
   certo enquanto os passos carregam, em vez de pular do rodapé para o topo. */
const PASSOS_PENDENTES_KEY = 'lemeai_sidebar_passos_pendentes';

/* Entre 703 e 1100px a sidebar fica recolhida independente da escolha do usuário */
/* Entre o fim do celular e 1100px a sidebar fica recolhida, independente da
   escolha do usuário. O piso acompanha o corte único (ver hooks/useCelular). */
const NARROW_QUERY = window.matchMedia(`(max-width: 1100px) and (min-width: ${CELULAR_MAX + 1}px)`);

const Sidebar: FC<SidebarProps> = ({ onLogout }) => {
    const location = useLocation();
    const { resumo, recarregarResumo } = useGlobalNotification();
    const { theme, toggleTheme } = useTheme();

    const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === 'true');
    const [isNarrow, setIsNarrow] = useState(() => NARROW_QUERY.matches);
    /* O botão de recolher está dentro da sidebar, então logo após o clique o
       ponteiro ainda está em cima dela e o auto-expandir a reabriria na hora.
       Ficamos ignorando o hover até o mouse sair. */
    const [ignoreHover, setIgnoreHover] = useState(false);
    const collapsed = isCollapsed || isNarrow;

    const toggleCollapsed = () => {
        const next = !isCollapsed;
        setIsCollapsed(next);
        setIgnoreHover(next);
    };
    const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
        marketing: false,
        /* Aberta por padrão: o valor dela é justamente as conversas estarem à
           vista para voltar a uma — fechada, o item vira só um rótulo. */
        assistente: true,
    });

    /* ─── CONVERSAS DO ASSISTENTE ─────────────────────────────────
       Carregadas uma vez e recarregadas quando alguma tela avisa que
       mudaram (conversa nova, renomeada ou removida). Sem o evento, a
       conversa criada na Home só apareceria aqui no próximo F5. */
    const [conversas, setConversas] = useState<ConversaAssistente[]>([]);

    const carregarConversas = useCallback(() => {
        AssistenteService.listarConversas(CONVERSAS_NO_MENU)
            .then(setConversas)
            /* Silencioso: é um menu, não a tela. Falhar aqui não pode
               atrapalhar quem está trabalhando em outra coisa. */
            .catch(() => setConversas([]));
    }, []);

    useEffect(() => {
        carregarConversas();
        window.addEventListener(CONVERSAS_ASSISTENTE_EVENT, carregarConversas);
        return () => window.removeEventListener(CONVERSAS_ASSISTENTE_EVENT, carregarConversas);
    }, [carregarConversas]);

    useEffect(() => {
        localStorage.setItem(COLLAPSED_KEY, String(isCollapsed));
    }, [isCollapsed]);

    useEffect(() => {
        const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
        NARROW_QUERY.addEventListener('change', onChange);
        return () => NARROW_QUERY.removeEventListener('change', onChange);
    }, []);

    /* Os três números vêm do contexto, que é quem mede (uma chamada por minuto, e
       mais uma quando chega mensagem). Aqui só se pede a medida de novo ao trocar de
       tela: sair da tela de Tarefas tendo resolvido três precisa apagar o número. */
    useEffect(() => {
        recarregarResumo();
    }, [location.pathname, recarregarResumo]);

    /* Abre a seção da rota atual sem fechar as que o usuário já deixou abertas */
    useEffect(() => {
        setOpenSections(prev => {
            const next = { ...prev };
            (Object.keys(SECTION_ROUTES) as SectionKey[]).forEach(key => {
                if (SECTION_ROUTES[key].some(route => location.pathname.startsWith(route))) {
                    next[key] = true;
                }
            });
            return next;
        });
    }, [location.pathname]);

    const toggleSection = (key: SectionKey) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

    /* ─── BUSCA ───────────────────────────────────────────────────
       O campo daqui virou GATILHO em 2026-10-01: quem busca é a paleta global
       (`components/BuscaGlobal.tsx`), desenhada por portal. O que morava aqui
       — duas listas inteiras baixadas no primeiro foco, filtro em memória só
       de oportunidade, painel de 320px preso à sidebar e nenhuma navegação por
       teclado — saiu junto. Ver `docs/propostas/busca-global-ctrl-k.html`. */
    const [menuConta, setMenuConta] = useState(false);
    const [planoAberto, setPlanoAberto] = useState(false);
    const [suporteAberto, setSuporteAberto] = useState(false);
    const refMenuConta = useRef<HTMLDivElement>(null);

    /* ─── BRIK NO CELULAR ──────────────────────────────────────────
       O "Instalar" do rodapé abre a janela com o celular (QR Code do /instalar) e o
       computador (o Brik como app). Fica sempre ali, mesmo
       para quem já instalou (decisão de 18/10/2026): serve para um segundo
       aparelho e para mandar o link à equipe. */
    const [qrAberto, setQrAberto] = useState(false);
    const [ancoraQr, setAncoraQr] = useState<HTMLElement | null>(null);
    const fecharQr = useCallback(() => setQrAberto(false), []);

    /* Menu da conta: fecha ao clicar fora e no Esc — mesmo comportamento do
       popover de filtro das listas. Só escuta enquanto está aberto. */
    useEffect(() => {
        if (!menuConta) return;
        const fora = (e: MouseEvent) => {
            if (refMenuConta.current && !refMenuConta.current.contains(e.target as Node)) {
                setMenuConta(false);
            }
        };
        const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuConta(false); };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', fora);
            document.removeEventListener('keydown', esc);
        };
    }, [menuConta]);

    /* Navegou: o menu não pode ficar aberto por cima da tela nova. */
    useEffect(() => { setMenuConta(false); }, [location.pathname]);

    /* O Ctrl+K saiu daqui: quem escuta agora é a paleta, que vale em qualquer
       tela — inclusive nas de Configurações, onde esta sidebar nem é desenhada.
       E ele não mexe mais no recolhimento: abrir a busca deixou de reorganizar
       a tela por baixo de quem só queria procurar. */

    /* ─── PRIMEIROS PASSOS ─────────────────────────────────────────
       O contador da pílula do rodapé. O estado vem do provider para não medir
       duas vezes (a tela usa o mesmo). Enquanto carrega, nada aparece: número
       piscando no rodapé chama mais atenção que o próprio item. */
    const { concluidos: passosConcluidos, total: passosTotal, carregando: passosCarregando } =
        usePrimeirosPassos();
    const mostrarContadorPassos =
        !passosCarregando && passosTotal > 0 && passosConcluidos < passosTotal;

    /* Com passo faltando, a pílula sobe para cima do menu, onde é vista primeiro;
       com tudo pronto, volta ao rodapé (pedido de 18/10/2026). Enquanto carrega,
       vale o último estado conhecido. */
    const passosPendentes = passosCarregando
        ? localStorage.getItem(PASSOS_PENDENTES_KEY) === 'true'
        : mostrarContadorPassos;
    useEffect(() => {
        if (!passosCarregando) localStorage.setItem(PASSOS_PENDENTES_KEY, String(mostrarContadorPassos));
    }, [passosCarregando, mostrarContadorPassos]);


    // ─── USUÁRIO E PERMISSÕES ─────────────────────────────────────
    /* O `can()` local saiu: quem responde "esta pessoa vê este item?" agora é
       `podeVerItem`, em `config/navegacao.ts`, com a mesma resposta para a
       sidebar e para o drawer do celular. */
    const permissions = getUserPermissions();

    let user: any = {};
    try {
        user = JSON.parse(localStorage.getItem('user') || '{}');
    } catch (e) {
        // ignore
    }
    const isEmpresa4Or8 = user?.empresaId === 4 || user?.empresaId === 8;
    /* A pílula de assinatura e o modal são só do administrador da empresa, e
       isso é papel, não permissão — ver `ehAdministradorDaEmpresa`. */
    const ehAdmin = ehAdministradorDaEmpresa();
    const resumoAssinatura = lerResumoAssinatura();
    const accountLabel = user?.nome || user?.email || 'Minha conta';
    const iniciaisConta = String(accountLabel).trim().split(/\s+/).slice(0, 2)
        .map((p: string) => p[0]?.toUpperCase() ?? '').join('') || '?';

    const isActive = (path: string) => location.pathname === path;

    const pilulaPassos = (
        <Link
            to="/primeiros-passos"
            className={`sb-pill ${passosPendentes ? 'sb-pill-topo' : ''} ${isActive('/primeiros-passos') ? 'active' : ''}`}
            title="Primeiros passos"
        >
            <LuRocket />
            <span>Primeiros passos</span>
            {/* O contador é o convite: quem tem pendência precisa
                saber disso ANTES de abrir a tela. Some quando está
                tudo pronto — pílula sem número é conta configurada.
                É um `<span>`, então a sidebar recolhida já o esconde
                junto com o rótulo, pela regra que existe. */}
            {mostrarContadorPassos && (
                <span className="sb-pill-cnt">{passosConcluidos}/{passosTotal}</span>
            )}
        </Link>
    );

    /* Quais itens esta pessoa alcança. A lista mora em `config/navegacao.ts` e é
       a mesma que o drawer do celular desenha — enquanto cada um teve a própria
       cópia, o celular ficou com 7 destinos contra os 20 daqui. Permissão e
       funcionalidade (o caso de Anúncios, que pede as duas) são decididas lá. */
    const itensPrincipais = itensVisiveis(GRUPOS_OPERACAO[0], permissions);
    const itensMarketing = itensVisiveis(GRUPOS_OPERACAO[1], permissions);
    /* `hasUsuarios`, `hasAdministracao`, `hasChatbot` e `hasEmpresa` saíram
       daqui: as quatro seções foram para a área de Configurações, e quem
       decide o que aparece lá é o `ConfiguracoesRail`, lendo o mesmo mapa de
       permissões a partir de `src/config/configuracoes.ts`. */

    /* Um item da navegação. O contador é do contexto; item sem `badge` não tem. */
    const renderItem = (item: ItemNav) => {
        const Icone = item.icone;
        const n = item.badge === 'tarefasVencidas' ? resumo.tarefasVencidas
            : item.badge === 'mensagensNaoLidas' ? resumo.mensagensNaoLidas
            : 0;
        return (
            <Link
                key={item.to}
                id={item.id}
                to={item.to}
                className={`sb-item ${isActive(item.to) ? 'active' : ''}`}
                title={item.rotulo}
            >
                <Icone />
                <span>{item.rotulo}</span>
                {n > 0 && <span className="sb-badge">{n > 99 ? '99+' : n}</span>}
            </Link>
        );
    };

    const renderSection = (key: SectionKey, title: string, children: ReactNode) => (
        <div className="sb-section">
            <button
                type="button"
                className={`sb-section-header ${openSections[key] ? '' : 'is-closed'}`}
                onClick={() => toggleSection(key)}
            >
                <LuChevronDown />
                <span>{title}</span>
            </button>
            <div className={`sb-section-body ${openSections[key] ? 'is-open' : ''}`}>
                <div className="sb-section-items">{children}</div>
            </div>
        </div>
    );

    return (
        /* O slot segura a largura no layout; o painel flutua sobre o conteúdo
           quando está recolhido e o mouse passa por cima. */
        <div
            className={`sidebar-slot ${collapsed ? 'is-collapsed' : ''} ${ignoreHover ? 'no-hover-expand' : ''}`}
            onMouseLeave={() => setIgnoreHover(false)}
        >
            <aside className="sidebar">

                <div className="sb-header">
                    <BrikMark className="sb-mark" />

                    <div className="sb-account-wrap" ref={refMenuConta}>
                        <button
                            id="sidebar-account"
                            type="button"
                            className="sb-account"
                            onClick={() => setMenuConta(v => !v)}
                            title={accountLabel}
                            aria-haspopup="menu"
                            aria-expanded={menuConta}
                        >
                            <span className="sb-account-name">{accountLabel}</span>
                            {/* A seta é o que promete a ação: sem ela o botão parecia um rótulo. */}
                            <LuChevronDown className="sb-account-caret" />
                        </button>

                        {menuConta && (
                            <div className="sb-menu" role="menu">
                                <div className="sb-menu-eu">
                                    <span className="sb-menu-av" aria-hidden="true">{iniciaisConta}</span>
                                    <span className="sb-menu-id">
                                        <span className="sb-menu-nome">{accountLabel}</span>
                                        {user?.email && <span className="sb-menu-email">{user.email}</span>}
                                    </span>
                                </div>

                                <div className="sb-menu-sep" />

                                {/* Uma porta só para a área inteira: conta, empresa,
                                    pessoas, vendas e chatbot moram todos lá agora. */}
                                <Link to="/configuracoes/conta" className="sb-menu-item" role="menuitem"
                                    onClick={() => setMenuConta(false)}>
                                    <LuSettings /> <span>Configurações</span>
                                </Link>

                                {!isEmpresa4Or8 && hasPermission(permissions, [PERMISSOES.plano]) && (
                                    <Link to="/plano" className="sb-menu-item" role="menuitem"
                                        onClick={() => setMenuConta(false)}>
                                        <LuCreditCard /> <span>Meu plano</span>
                                    </Link>
                                )}

                                {/* Com a configuração pronta, Primeiros passos sai da sidebar e
                                    mora aqui (pedido de 18/10/2026): quem quer rever acha, e o
                                    rodapé fica só com plano e suporte. Faltando passo, a porta é
                                    a pílula azul em cima de Home. */}
                                {!passosPendentes && (
                                    <Link to="/primeiros-passos" className="sb-menu-item" role="menuitem"
                                        onClick={() => setMenuConta(false)}>
                                        <LuRocket /> <span>Primeiros passos</span>
                                    </Link>
                                )}

                                <div className="sb-menu-sep" />

                                <Link to="/help" className="sb-menu-item" role="menuitem"
                                    onClick={() => setMenuConta(false)}>
                                    <LuCircleHelp /> <span>Ajuda</span>
                                </Link>

                                {/* Não fecha o menu ao clicar: o efeito é a tela inteira
                                    mudando de cor, e fechar junto esconderia o
                                    interruptor bem na hora de conferir o resultado. */}
                                <button
                                    type="button"
                                    className="sb-menu-item"
                                    role="menuitemcheckbox"
                                    aria-checked={theme === 'dark'}
                                    onClick={toggleTheme}
                                >
                                    {theme === 'light' ? <LuMoon /> : <LuSun />}
                                    <span>Modo escuro</span>
                                    <span className={`sb-menu-switch ${theme === 'dark' ? 'is-on' : ''}`}
                                        aria-hidden="true"><i /></span>
                                </button>

                                <div className="sb-menu-sep" />

                                <button type="button" className="sb-menu-item" role="menuitem"
                                    onClick={() => { setMenuConta(false); onLogout(); }}>
                                    <LuLogOut /> <span>Sair</span>
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className="sb-icon-btn sb-collapse-btn"
                        onClick={toggleCollapsed}
                        title={collapsed ? 'Expandir menu' : 'Recolher menu'}
                        aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
                    >
                        <LuPanelLeft />
                    </button>
                </div>

                <div className="sb-search-row">
                    {/* Gatilho, não campo: a mesma aparência de antes (inclusive
                        as teclas desenhadas), mas clicar abre a paleta. Botão
                        de verdade para o teclado e o leitor de tela chegarem
                        nele — era um `input` que não levava a lugar nenhum
                        sem o mouse. */}
                    <button
                        type="button"
                        className="sb-search"
                        onClick={abrirBuscaGlobal}
                        aria-label="Buscar na plataforma"
                        aria-keyshortcuts="Control+K"
                    >
                        <span className="sb-search-trigger"><LuSearch /></span>
                        <span className="sb-search-rotulo">Buscar</span>
                        <span className="sb-kbd-group">
                            <kbd className="sb-kbd">CTRL</kbd>
                            <kbd className="sb-kbd">K</kbd>
                        </span>
                    </button>

                    <NotificationsBell naoLidos={resumo.avisosNaoLidos} onContagemMudou={recarregarResumo} />
                </div>

                {passosPendentes && <div className="sb-passos-topo">{pilulaPassos}</div>}

                <nav className="sb-nav">
                    {/* Os itens, a ordem, as permissões e os contadores vêm de
                        `config/navegacao.ts`. As duas rotas antigas de Relatórios
                        (`/relatorios/vendas` e `/relatorios/campanhas`) continuam
                        valendo e redirecionam, para link salvo não quebrar. */}
                    {itensPrincipais.map(renderItem)}

                    {itensMarketing.length > 0 && renderSection('marketing', 'Marketing', (
                        <>{itensMarketing.map(renderItem)}</>
                    ))}

                    {/* O histórico do assistente. Sem `can()` pelo mesmo motivo da
                        Home: todo perfil tem, e o alcance é decidido na API. */}
                    {renderSection('assistente', 'Brik AI', (
                        <>
                            {conversas.map(c => (
                                <Link
                                    key={c.conversaId}
                                    to={`/assistente/${c.conversaId}`}
                                    className={`sb-item ${isActive(`/assistente/${c.conversaId}`) ? 'active' : ''}`}
                                    title={c.titulo}
                                >
                                    <LuMessageSquare />
                                    <span>{c.titulo}</span>
                                </Link>
                            ))}
                            <Link
                                to="/assistente"
                                className={`sb-item ${location.pathname === '/assistente' ? 'active' : ''}`}
                                title={conversas.length > 0 ? 'Ver todas as conversas' : 'Conversas do assistente'}
                            >
                                <LuHistory />
                                <span>{conversas.length > 0 ? 'Ver todas' : 'Nenhuma conversa ainda'}</span>
                            </Link>
                        </>
                    ))}

                </nav>

                {/* Rodapé em linhas (pedido de 18/10/2026, com print de referência):
                    filete suave em cima, e cada porta é uma linha com ícone, nome e
                    seta — as pílulas saíram. */}
                <div className="sb-footer">
                    {/* A assinatura mora aqui: é o que ele pediu para manter ("quero
                        manter a informação na sidebar de dias restantes, teste") e a
                        porta para o modal. Só o administrador da empresa vê — decisão
                        dele, e não é configurável, por isso é papel e não permissão.
                        Custo de API: zero, tudo vem do `Auth/Me`. Em teste e vencida o
                        texto e o ícone ganham a cor do aviso. */}
                    {ehAdmin && (
                        <button
                            type="button"
                            className={`sb-item sb-rodape-item is-${resumoAssinatura.estado}`}
                            onClick={() => setPlanoAberto(true)}
                            title="Assinatura"
                        >
                            {resumoAssinatura.estado === 'trial' ? <LuClock /> : null}
                            {resumoAssinatura.estado === 'vencida' ? <LuTriangleAlert /> : null}
                            {resumoAssinatura.estado === 'ativa' ? <LuCreditCard /> : null}
                            <span>
                                {resumoAssinatura.estado === 'trial'
                                    ? `Teste grátis${resumoAssinatura.diasRestantes != null
                                        ? ` · ${resumoAssinatura.diasRestantes} ${resumoAssinatura.diasRestantes === 1 ? 'dia' : 'dias'}`
                                        : ''}`
                                    : resumoAssinatura.estado === 'vencida'
                                    ? 'Assinatura vencida'
                                    : 'Meu plano'}
                            </span>
                            <LuChevronRight className="sb-rodape-seta" />
                        </button>
                    )}

                    {/* Todo perfil vê: quem usa o dia inteiro, e acha o defeito, é o
                        vendedor. Abre a modal do WhatsApp + caixinha de sugestões. */}
                    <button
                        type="button"
                        className="sb-item sb-rodape-item"
                        onClick={() => setSuporteAberto(true)}
                        title="Central de ajuda"
                    >
                        <LuCircleHelp />
                        <span>Central de ajuda</span>
                        <LuChevronRight className="sb-rodape-seta" />
                    </button>

                    <button
                        type="button"
                        className="sb-item sb-rodape-item"
                        onClick={e => {
                            if (qrAberto) { setQrAberto(false); return; }
                            setAncoraQr(e.currentTarget);
                            setQrAberto(true);
                        }}
                        title="Instalar o Brik no celular ou no computador"
                        aria-expanded={qrAberto}
                    >
                        <LuDownload />
                        <span>Instalar aplicativo</span>
                        <LuChevronRight className="sb-rodape-seta" />
                    </button>
                </div>
            </aside>

            <PlanoModal open={planoAberto} onClose={() => setPlanoAberto(false)} />
            <SuporteModal open={suporteAberto} onClose={() => setSuporteAberto(false)} />
            <InstalarBrik aberto={qrAberto} ancora={ancoraQr} onClose={fecharQr} />
        </div>
    );
};

export default Sidebar;
